/**
 * Inventory API DTOs
 * Based on https://buynow.lucidmotors.com/api/v4/en_us/store/inventory/vehicles
 */

/*
|--------------------------------------------------------------------------
| LOCATION TYPES
|--------------------------------------------------------------------------
*/

export type WhoAmIMessage = {
  IP: string;
  Country: string;
  Region: string;
  Timezone: string;
  Postcode: string;
  Lat: string;
  Long: string;
};

export type WhoAmIResponse = {
  msg: WhoAmIMessage;
};

export type UserLocation = {
  lat: number;
  long: number;
  region: string;
  postcode: string;
  country: string;
};

/*
|--------------------------------------------------------------------------
| PR CODES TYPES (CPQ Configurator)
|--------------------------------------------------------------------------
*/

export type PrCodePrice = {
  currency: string;
  trim: string;
  amountsByCountry: {
    availableFrom: string;
    countryCode: string;
    showPrice: boolean;
    basePrice: number;
    value: number;
    validFrom: string;
    validTo: string;
  }[];
};

export type PrCodeItem = {
  name: string;
  description: string | null;
  productType: string;
  displayOrder: number;
  prices: PrCodePrice[];
  technicalName: string;
  technicalDescription: string;
  typeId: string;
  typeName: string;
  image?: string;
  modal_description?: string;
};

export type PrCodesResponse = Record<string, PrCodeItem>;

export type PrCodeTypeId =
  | 'LUCID_EXT_COLOR'
  | 'LUCID_INT_THEME'
  | 'LUCID_WHEELS'
  | 'LUCID_APPEARANCE'
  | 'LUCID_TRIM'
  | 'LUCID_DREAMDRIVE'
  | 'LUCID_SOUNDSYSTEM'
  | 'LUCID_SEATING'
  | 'LUCID_PACKAGES'
  | 'LUCID_MODEL_VARIANT';

/*
|--------------------------------------------------------------------------
| PRICE BOOK PR CODES TYPES (Importer)
|--------------------------------------------------------------------------
*/

export type PriceBookPrCodesRequest = {
  channel: string;
  model: string;
  year: string;
  locale: string;
};

/*
|--------------------------------------------------------------------------
| INVENTORY TABLE ROW (for UI display)
|--------------------------------------------------------------------------
*/

export type InventoryTableRow = {
  vin: string;
  webLink: string;
  batchNumber: string;
  availability: string;
  locationName: string;
  city: string;
  country: string;
  model: string;
  trim: string;
  drivetrain: string;
  exteriorColor: string;
  exteriorColorCode: string;
  interiorColor: string;
  interiorColorCode: string;
  appearance: string;
  appearanceCode: string;
  roof: string;
  wheels: string;
  wheelsCode: string;
  year: string;
  adas: string;
  adasCode: string;
  soundSystem: string;
  soundSystemCode: string;
  frontSeats: string;
  frontSeatsCode: string;
  rearSeats: string;
  comfortPackage: string;
  dynamicHandlingPackage: string;
  technologyPackage: string;
  towingPackage: string;
  range: string;
  power: string;
  zeroToHundredKmh: string;
  transportationConfirmed: string;
  inTransitToLocation: string;
  activeFactoryCampaigns: string;
  uuid: string;
  shortcode: string;
  condition: 'new' | 'demo' | 'pre-owned';
  baseMsrp?: number;
  price?: number;
  destinationCharge?: number;
  finalPrice?: number;
  imageUrl?: string;
};

/*
|--------------------------------------------------------------------------
| VEHICLE OPTION TYPES
|--------------------------------------------------------------------------
*/

export type VehicleOption = {
  typeId: string;
  sapCode: string;
  zlCode: string;
  technicalName: string;
};

/*
|--------------------------------------------------------------------------
| VEHICLE ADDRESS
|--------------------------------------------------------------------------
*/

export type VehicleAddress = {
  addressLine1: string;
  locationName: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string | null;
  distanceinMeters: number;
  locationType: string;
  greaterArea: string;
};

/*
|--------------------------------------------------------------------------
| VEHICLE OFFERS
|--------------------------------------------------------------------------
*/

export type VehicleCredit = {
  key: string;
  value: number;
  apply: boolean;
  label: string;
  disable: boolean;
  availableVehicles?: boolean;
  webCalc?: boolean;
};

export type VehicleOffers = {
  financeTerm: number;
  leaseTerm: number;
  financeRate: number;
  leaseAmount: number;
  amtDueAtLeaseSign: number;
  discText: string[];
};

export type VehicleCredits = {
  lease: VehicleCredit[];
  finance?: VehicleCredit[];
  cash?: VehicleCredit[];
};

/*
|--------------------------------------------------------------------------
| VEHICLE PRICING
|--------------------------------------------------------------------------
*/

export type VehiclePricing = {
  baseMSRP?: number;
  totalMSRP?: number;
  destinationCharge?: number;
  finalPrice?: number;
};

/*
|--------------------------------------------------------------------------
| INVENTORY VEHICLE
|--------------------------------------------------------------------------
*/

export type InventoryVehicle = {
  uuid: string;
  titleStatus: string;
  batchNumber: string;
  VIN: string;
  category: string;
  subCategory: string;
  shortcode: string;
  salesStatus: string | null;
  availability: string;
  model: VehicleOption;
  modelVariant: VehicleOption;
  color: VehicleOption;
  interior: VehicleOption;
  wheels: VehicleOption;
  roof: VehicleOption;
  look: VehicleOption;
  motorDrive: VehicleOption;
  edition: VehicleOption;
  country: VehicleOption;
  region: VehicleOption;
  seating: VehicleOption;
  packages: VehicleOption;
  vehiclePackage: VehicleOption;
  driverAssistance: VehicleOption;
  soundSystem: VehicleOption;
  seatingOptions: VehicleOption;
  appearance: VehicleOption;
  modelYear: string;
  additionalFeatures: {
    soundSystem: VehicleOption;
    dreamDrive: VehicleOption;
  };
  discountPercentage: number;
  featuredScore: number;
  actual_factory_gate_date: string;
  serviceCampaignCount: number;
  rearSeats: {
    backSeatsCode: string;
    backSeats: string;
  };
  address: VehicleAddress;
  isInTransit: boolean;
  isAtStudio: boolean;
  isAtHub: boolean;
  distance: number;
  offers: VehicleOffers;
  credits: VehicleCredits;
  pricing?: VehiclePricing;
};

/*
|--------------------------------------------------------------------------
| REQUEST PARAMS
|--------------------------------------------------------------------------
*/

export type InventoryVehiclesParams = {
  long: number;
  lat: number;
  type?: 'new' | 'demo' | 'pre-owned';
  modelVariant?: string;
  modelYear?: string;
  availability?: string;
  sortType?: 'distance' | 'price' | 'modelYear';
  distance?: number;
  zipState?: string;
  requestedVehiclesNum?: number;
  enabled?: boolean;
};

/*
|--------------------------------------------------------------------------
| FILTER TYPES
|--------------------------------------------------------------------------
*/

export type FilterOption = {
  label: string;
  value: string;
  image?: string;
  color?: string;
  availableTrims?: string[];
};

export type FilterConfig = {
  condition: FilterOption[];
  trim: FilterOption[];
  searchWithin: FilterOption[];
  availability: FilterOption[];
  exteriorColor: FilterOption[];
  interiorTheme: FilterOption[];
  wheels: FilterOption[];
  appearance: FilterOption[];
  driverAssistance: FilterOption[];
  soundSystem: FilterOption[];
  seating: FilterOption[];
  packages: FilterOption[];
};

export type InventoryFilters = {
  condition?: string;
  model?: string[];
  trim?: string[];
  searchWithin?: string;
  availability?: string[];
  exteriorColor?: string[];
  interiorTheme?: string[];
  wheels?: string[];
  appearance?: string[];
  driverAssistance?: string[];
  soundSystem?: string[];
  seating?: string[];
  packages?: string[];
  vehiclePackage?: string[];
  dynamicPackage?: string[];
  techPackage?: string[];
  towingPackage?: string[];
};

/*
|--------------------------------------------------------------------------
| RESPONSE
|--------------------------------------------------------------------------
*/

export type InventoryVehiclesResponse = {
  data: InventoryVehicle[];
};

/*
|--------------------------------------------------------------------------
| CHECKOUT DETAIL TYPES
|--------------------------------------------------------------------------
*/

export type CheckoutDetailRequest = {
  batchNumber: string;
  uuid: string;
  type: 'new' | 'demo' | 'pre-owned';
  zipState: string;
  shortCode?: string;
};

export type CheckoutByShortcodeRequest = {
  shortCode: string;
  orderChannel?: string;
};

export type CheckoutVehicleInfo = {
  label: string;
  type: string;
  code: string;
  name: string;
  value: number;
  description?: string;
  availableFrom?: string | null;
  availableFromPretty?: string | null;
  contents?: CheckoutVehicleInfo[];
};

export type CheckoutPricingItem = {
  name: string;
  value: number;
  taxAmount?: number | null;
  valueAfterTax?: number | null;
  code?: string;
};

export const NL_NON_TAXABLE_FEE_CODES: ReadonlySet<string> = new Set(['BPM_TAX', 'REG_COST']);
export const NL_TAXABLE_FEE_CODES: ReadonlySet<string> = new Set(['RCYL_FEE']);
export const NL_STATUTORY_FEE_CODES: ReadonlySet<string> = new Set([
  ...NL_NON_TAXABLE_FEE_CODES,
  ...NL_TAXABLE_FEE_CODES,
]);

export type CheckoutPricingTax = {
  name: string;
  rate: number;
  value: number;
  taxAmount?: number | null;
  valueAfterTax?: number | null;
  optionsInformation?: { productName: string; value: number; unitPriceBeforeTax?: number }[];
};

export type CheckoutPricing = {
  currencyCode: string;
  addOns: CheckoutPricingItem;
  subtotal: CheckoutPricingItem;
  configurationSubtotal?: CheckoutPricingItem;
  fees: CheckoutPricingItem[];
  credits: CheckoutPricingItem[];
  deposit: CheckoutPricingItem;
  total: CheckoutPricingItem;
  tax?: CheckoutPricingTax;
};

export type CheckoutData = {
  vin: string;
  model: string;
  modelYear: string;
  trim: string;
  shortcode: string;
  type: string;
  location: string | null;
  availability: string;
  shortcodeCountryCode: string;
  pricing: CheckoutPricing;
  vehicleInfo: CheckoutVehicleInfo[];
  additionalDisclaimers: string[];
};

export type CheckoutDetailResponse = {
  vehicleDetailsData: InventoryVehicle & {
    zlList: string;
    price: {
      value: number;
      currency: string;
      afterDiscountPrice: number;
    };
    isCPO: boolean;
    features: unknown[];
    upgrades: unknown[];
    imageUrl?: string; // Backend-provided vehicle image URL
    acWebUrl?: string; // Backend-provided web URL
  };
  checkoutData: CheckoutData;
  isExpiredShortCode: boolean;
};

/*
|--------------------------------------------------------------------------
| VEHICLE SEARCH API (Auctus)
|--------------------------------------------------------------------------
*/

export type VehicleConfigOption = {
  code: string | null;
  name: string;
  localizedName?: string;
};

export type VehicleConfig = {
  appearance: VehicleConfigOption;
  wheels: VehicleConfigOption;
  exteriorColor: VehicleConfigOption;
  interiorColor: VehicleConfigOption;
  roof: VehicleConfigOption;
  driveTrain: VehicleConfigOption;
  dreamDrive: VehicleConfigOption;
  soundSystem: VehicleConfigOption;
  variant: VehicleConfigOption;
  bundle?: VehicleConfigOption;
  model: VehicleConfigOption;
  frontSeats: VehicleConfigOption;
  rearSeats: VehicleConfigOption;
  comfortConvenience: VehicleConfigOption;
  dynamicPackage: VehicleConfigOption;
  techPackage: VehicleConfigOption;
  towingPackage: VehicleConfigOption;
  edition: VehicleConfigOption;
};

export type VehicleMetadata = {
  modelYear: number;
  vin: string;
  batchNumber: string;
  subCategory: string;
  factoryCampaignCount: number;
  odometer: string;
  stateOfCharge: string | null;
  otaStatus: string | null;
  otaVersion: string | null;
  lastTelemeteryUpdate: string | null;
  locationName: string;
  state: string;
  city: string;
  country: string;
  transitCountry: string;
  transportationConfirmed: string | null;
  range?: string | number | null;
  power?: string | number | null;
  zeroToHundredKmh?: string | number | null;
  accelaration?: string | number | null;
  inTransitToLocation?: string | null;
  availability?: string;
  totalPrice: number;
  currency: string;
  inventoryStatus: string | null;
  displayVehicle: boolean;
  shortcode: string;
  uuid: string;
  acWebUrl?: string;
  imageUrl?: string;
};

export type SearchVehicle = {
  vehicleConfig: VehicleConfig;
  metadata: VehicleMetadata;
};

export type VehicleSearchFilters = {
  // Global search filters
  vehicleIdentifier?: string; // Searches both VIN and Batch Number
  shortCode?: string;
  uuid?: string;
  // Code-specific filters
  appearance?: string;
  wheels?: string;
  exteriorColor?: string;
  interiorColor?: string;
  roof?: string;
  driveTrain?: string;
  dreamDrive?: string;
  soundSystem?: string;
  variant?: string;
  bundle?: string;
  model?: string;
  frontSeats?: string;
  rearSeats?: string;
  vehiclePackage?: string;
  dynamicPackage?: string;
  techPackage?: string;
  towingPackage?: string;
  edition?: string;
  modelYear?: string;
  availability?: string;
  // Legacy filters (kept for backward compatibility)
  vin?: string;
  batchNumber?: string;
};

export type VehicleSearchRequest = {
  page: number;
  size: number;
  sortBy?: string; // e.g., "batchNumber", "modelYear", "vin"
  sortOrder?: 'asc' | 'desc';
  filters?: VehicleSearchFilters;
};

export type VehicleSearchSort = {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
};

export type VehicleSearchPageable = {
  pageNumber: number;
  pageSize: number;
  sort: VehicleSearchSort;
  offset: number;
  paged: boolean;
  unpaged: boolean;
};

export type VehicleSearchResponse = {
  totalPages: number;
  totalElements: number;
  size: number;
  content: SearchVehicle[];
  numberOfElements: number;
  number: number;
  sort: VehicleSearchSort;
  first: boolean;
  last: boolean;
  pageable: VehicleSearchPageable;
  empty: boolean;
};
